<template>
  <Teleport to="body">
    <Transition name="insufficient-coins-modal" @after-leave="handleAfterLeave">
      <div
        v-if="modelValue"
        class="insufficient-coins-overlay"
        role="presentation"
        @click.self="handleOverlayClick"
      >
        <section
          class="insufficient-coins-dialog"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="descriptionId"
        >
          <button
            class="insufficient-coins-close"
            type="button"
            :aria-label="closeLabel"
            @click="handleCancel"
          >
            <SvgIcon name="modal-close" :size="12" />
          </button>

          <SvgIcon class="insufficient-coins-illustration" name="insufficient-coins" />

          <div class="insufficient-coins-content">
            <h2 :id="titleId" class="insufficient-coins-title">{{ title }}</h2>
            <div :id="descriptionId" class="insufficient-coins-description">
              <p>
                <span>{{ actionText }}需要 </span>
                <strong>{{ formatCoins(requiredCoins) }} 云币</strong>
              </p>
              <p>
                <span>当前余额 </span>
                <strong>{{ formatCoins(currentCoins) }} 云币</strong>
                <span>，还差 </span>
                <strong class="insufficient-coins-shortfall">
                  {{ formatCoins(displayShortfallCoins) }} 云币
                </strong>
              </p>
            </div>
          </div>

          <div class="membership-banner">
            <div class="membership-banner-content">
              <span class="membership-icon-wrap" aria-hidden="true">
                <SvgIcon name="membership-crown" :size="18" />
              </span>
              <div class="membership-copy">
                <strong>升级会员更划算</strong>
                <span>
                  享{{ discountLabel }}折优惠，仅需
                  <em>{{ formatCoins(displayMemberCoins) }} 云币</em>
                </span>
              </div>
            </div>
            <button class="membership-upgrade-button" type="button" @click="handleUpgrade">
              {{ upgradeText }} &gt;
            </button>
          </div>

          <div class="insufficient-coins-actions">
            <button
              class="insufficient-coins-button insufficient-coins-button--cancel"
              type="button"
              @click="handleCancel"
            >
              {{ cancelText }}
            </button>
            <button
              class="insufficient-coins-button insufficient-coins-button--recharge"
              type="button"
              @click="handleRecharge"
            >
              {{ rechargeText }}
            </button>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, useId, watch } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';

  interface Props {
    modelValue: boolean;
    actionText: string;
    requiredCoins: number;
    currentCoins: number;
    shortfallCoins?: number;
    memberCoins?: number;
    title?: string;
    discountLabel?: string;
    cancelText?: string;
    rechargeText?: string;
    upgradeText?: string;
    closeLabel?: string;
    closeOnOverlay?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    shortfallCoins: undefined,
    memberCoins: undefined,
    title: '云币不足',
    discountLabel: '7',
    cancelText: '取消',
    rechargeText: '充值云币',
    upgradeText: '去升级',
    closeLabel: '关闭云币不足提示',
    closeOnOverlay: true
  });

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    cancel: [];
    recharge: [];
    upgrade: [];
    closed: [reason: 'cancel' | 'recharge' | 'upgrade'];
  }>();

  const id = useId();
  const closeReason = ref<'cancel' | 'recharge' | 'upgrade' | null>(null);
  const closing = ref(false);
  const titleId = `insufficient-coins-title-${id}`;
  const descriptionId = `insufficient-coins-description-${id}`;
  const displayShortfallCoins = computed(
    () => props.shortfallCoins ?? Math.max(props.requiredCoins - props.currentCoins, 0)
  );
  const displayMemberCoins = computed(
    () => props.memberCoins ?? Math.ceil(props.requiredCoins * 0.7)
  );

  function formatCoins(value: number) {
    return new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 }).format(value);
  }

  function closeModal() {
    emit('update:modelValue', false);
  }

  function handleCancel() {
    if (closing.value) return;
    closing.value = true;
    closeReason.value = 'cancel';
    emit('cancel');
    closeModal();
  }

  function handleRecharge() {
    if (closing.value) return;
    closing.value = true;
    closeReason.value = 'recharge';
    emit('recharge');
    closeModal();
  }

  function handleUpgrade() {
    if (closing.value) return;
    closing.value = true;
    closeReason.value = 'upgrade';
    emit('upgrade');
    closeModal();
  }

  function handleAfterLeave() {
    if (!closeReason.value) return;

    emit('closed', closeReason.value);
    closeReason.value = null;
    closing.value = false;
  }

  function handleOverlayClick() {
    if (props.closeOnOverlay) handleCancel();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && props.modelValue) handleCancel();
  }

  watch(
    () => props.modelValue,
    (visible) => {
      if (visible) {
        closeReason.value = null;
        closing.value = false;
        document.addEventListener('keydown', handleKeydown);
      } else {
        document.removeEventListener('keydown', handleKeydown);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown);
  });
</script>

<style scoped>
  .insufficient-coins-overlay {
    position: fixed;
    inset: 0;
    z-index: 4000;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 24px;
    background: rgba(0, 0, 0, 0.4);
  }

  .insufficient-coins-dialog {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    width: 460px;
    max-width: 100%;
    box-sizing: border-box;
    padding: 40px 32px;
    border-radius: 24px;
    background: #fff;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    color: #160211;
    font-family: 'Noto Sans SC', 'Source Han Sans CN VF', 'Microsoft YaHei', sans-serif;
  }

  .insufficient-coins-close {
    position: absolute;
    top: 16px;
    right: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: #f5f0ff;
    color: #6e6a7c;
    cursor: pointer;
  }

  .insufficient-coins-close:hover {
    background: #ece4ff;
    color: #6033e1;
  }

  .insufficient-coins-illustration {
    width: 140px;
    height: 97px;
  }

  .insufficient-coins-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .insufficient-coins-title {
    margin: 0;
    color: #160211;
    font-family: Inter, 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
  }

  .insufficient-coins-description {
    color: #777386;
    font-size: 15px;
    font-weight: 400;
    line-height: 25px;
    text-align: center;
  }

  .insufficient-coins-description p {
    margin: 0;
  }

  .insufficient-coins-description p + p {
    margin-top: 4px;
  }

  .insufficient-coins-description strong {
    color: #160211;
    font-style: normal;
    font-weight: 700;
  }

  .insufficient-coins-description .insufficient-coins-shortfall {
    color: #f04420;
  }

  .membership-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-height: 64px;
    box-sizing: border-box;
    padding: 12px 16px;
    border-radius: 16px;
    background: #f0ebfd;
  }

  .membership-banner-content {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .membership-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    border-radius: 50%;
    background: #ebe2fe;
  }

  .membership-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 17px;
  }

  .membership-copy strong {
    color: #5b2ce2;
    font-size: 13px;
    font-weight: 600;
  }

  .membership-copy span {
    color: #777386;
    font-size: 12px;
    white-space: nowrap;
  }

  .membership-copy em {
    color: #f5a623;
    font-style: normal;
  }

  .membership-upgrade-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 85px;
    height: 38px;
    flex: 0 0 auto;
    box-sizing: border-box;
    padding: 0 16px;
    border: 1px solid #e6ddfc;
    border-radius: 10px;
    background: #faf8fe;
    color: #6033e1;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }

  .membership-upgrade-button:hover {
    border-color: #cbbaf5;
    background: #fff;
  }

  .insufficient-coins-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
  }

  .insufficient-coins-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 48px;
    padding: 0 16px;
    border-radius: 14px;
    font: inherit;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
  }

  .insufficient-coins-button--cancel {
    border: 1px solid #e0d8f5;
    background: #fff;
    color: #6e6a7c;
  }

  .insufficient-coins-button--cancel:hover {
    border-color: #cbbaf5;
    background: #fbf9ff;
  }

  .insufficient-coins-button--recharge {
    border: 1px solid #6033e1;
    background: #6033e1;
    color: #fff;
  }

  .insufficient-coins-button--recharge:hover {
    border-color: #5128c7;
    background: #5128c7;
  }

  .insufficient-coins-close:focus-visible,
  .membership-upgrade-button:focus-visible,
  .insufficient-coins-button:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .insufficient-coins-modal-enter-active,
  .insufficient-coins-modal-leave-active {
    transition: opacity 0.18s ease;
  }

  .insufficient-coins-modal-enter-active .insufficient-coins-dialog,
  .insufficient-coins-modal-leave-active .insufficient-coins-dialog {
    transition:
      opacity 0.18s ease,
      transform 0.18s ease;
  }

  .insufficient-coins-modal-enter-from,
  .insufficient-coins-modal-leave-to {
    opacity: 0;
  }

  .insufficient-coins-modal-enter-from .insufficient-coins-dialog,
  .insufficient-coins-modal-leave-to .insufficient-coins-dialog {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }

  @media (max-width: 520px) {
    .insufficient-coins-overlay {
      padding: 16px;
    }

    .insufficient-coins-dialog {
      gap: 20px;
      padding: 32px 20px 24px;
      border-radius: 20px;
    }

    .insufficient-coins-close {
      top: 12px;
      right: 12px;
    }

    .membership-banner {
      gap: 8px;
      padding: 12px;
    }

    .membership-banner-content {
      gap: 8px;
    }

    .membership-icon-wrap {
      width: 36px;
      height: 36px;
      flex-basis: 36px;
    }

    .membership-copy strong {
      font-size: 12px;
    }

    .membership-copy span {
      font-size: 11px;
    }

    .membership-upgrade-button {
      min-width: 72px;
      padding: 0 10px;
      font-size: 12px;
    }
  }

  @media (max-width: 360px) {
    .membership-copy span {
      white-space: normal;
    }
  }
</style>
